import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Award, 
  BadgeCheck, 
  Target, 
  Heart, 
  Clock, 
  FileCheck,
  ArrowRight,
  ChevronRight,
  Star,
  ShieldCheck,
  MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { aboutContent, images } from '../data/mock';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
                Chi Siamo
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-6 leading-tight">
                {aboutContent.subtitle}
              </h1>
              <div className="text-xl text-slate-600 leading-relaxed mb-8 space-y-4">
                {aboutContent.story.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Award className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-800">30+</p>
                    <p className="text-slate-500 text-sm">Anni di esperienza</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-7 h-7 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-800">1000+</p>
                    <p className="text-slate-500 text-sm">Clienti soddisfatti</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={images.hero}
                  alt="Pavimento in parquet G&B Wood"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-amber-100 rounded-2xl -z-10" />
              <div className="absolute -top-6 -right-6 w-28 h-28 bg-amber-600/20 rounded-2xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative lg:order-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src={images.parquetStack}
                    alt="Materiali parquet"
                    className="w-full h-48 object-cover rounded-xl shadow-lg"
                  />
                  <img
                    src={images.craftsman3}
                    alt="Lavorazione legno"
                    className="w-full h-64 object-cover rounded-xl shadow-lg"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img
                    src={images.woodSamples}
                    alt="Campioni legno"
                    className="w-full h-64 object-cover rounded-xl shadow-lg"
                  />
                  <img
                    src={images.modernStudy}
                    alt="Interno con parquet"
                    className="w-full h-48 object-cover rounded-xl shadow-lg"
                  />
                </div>
              </div>
            </div>

            <div className="lg:order-1">
              <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
                La Nostra Missione
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
                Trasformare gli spazi con la bellezza del legno
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {aboutContent.mission}
              </p>

              <div className="space-y-4">
                {aboutContent.values.map((value, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-amber-50 transition-colors">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <ChevronRight className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">{value.title}</h3>
                      <p className="text-slate-600 text-sm">{value.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-amber-400 font-semibold text-sm uppercase tracking-wider mb-4">
              I Nostri Valori
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Perché sceglierci
            </h2>
            <p className="text-slate-300 text-lg">
              Esperienza, qualità e garanzia: i pilastri su cui si basa il nostro lavoro 
              per offrirti il miglior servizio possibile.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {aboutContent.certifications.map((cert, index) => {
              let IconComponent;
              switch(cert.icon) {
                case 'Award':
                  IconComponent = Award;
                  break;
                case 'Star':
                  IconComponent = Star;
                  break;
                case 'Heart':
                  IconComponent = Heart;
                  break;
                case 'MessageCircle':
                  IconComponent = MessageCircle;
                  break;
                default:
                  IconComponent = ShieldCheck;
              }
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700 hover:border-amber-500/50 transition-all">
                  <CardContent className="p-8">
                    <div className="w-20 h-20 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <IconComponent className="w-10 h-10 text-amber-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-4 text-center">{cert.title}</h3>
                    <p className="text-slate-300 text-center leading-relaxed">{cert.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team/Values Section */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-4">
              Chi Siamo
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
              Carmine Avilio
            </h2>
            <p className="text-slate-600 text-lg">
              Artigiano appassionato, dedito alla cura di ogni dettaglio 
              e alla soddisfazione di ogni cliente.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="bg-white border-0 shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="h-80 md:h-auto bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center p-8">
                  <div className="w-40 h-40 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-5xl font-bold text-amber-600">CA</span>
                  </div>
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <h3 className="text-2xl font-semibold text-slate-800 mb-2">Carmine Avilio</h3>
                  <p className="text-amber-600 font-medium mb-4">Fondatore & Master Posatore</p>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Con oltre 30 anni di esperienza nella posa parquet e lavorazioni in legno, 
                    ho dedicato la mia vita a trasformare gli spazi con la bellezza del legno naturale.
                  </p>
                  <p className="text-slate-600 leading-relaxed">
                    Specializzato in pose artistiche, recupero pavimenti storici e lavorazioni 
                    su misura. Ogni progetto è per me un'opera d'arte.
                  </p>
                </CardContent>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-amber-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Vuoi conoscerci meglio?
          </h2>
          <p className="text-amber-100 text-lg mb-8">
            Contattaci per un sopralluogo gratuito. Saremo felici di mostrarti 
            i nostri lavori e discutere del tuo progetto.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/preventivi">
              <Button size="lg" className="w-full sm:w-auto bg-white text-amber-700 hover:bg-amber-50">
                Richiedi Preventivo
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                Vedi i nostri lavori
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
